import { useState, useCallback } from "react";
import {
  Building2,
  Key,
  Webhook,
  Flag,
  Shield,
  Database,
  Activity,
  AlertTriangle,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import {
  useEnterpriseApiKeys,
  useEnterpriseWebhooks,
  useFeatureFlags,
  useGDPRRequests,
  useBackup,
  usePerformanceMetrics,
} from "@/hooks/admin/useEnterprise";
import type {
  EnterpriseApiKey,
  EnterpriseWebhook,
  FeatureFlag,
  GDPRRequest,
  BackupJob,
  PerformanceMetric,
} from "@/lib/admin-api/enterprise";

// ---------------------------------------------------------------------------
// Tab definition
// ---------------------------------------------------------------------------

type TabKey =
  | "api-keys"
  | "webhooks"
  | "flags"
  | "gdpr"
  | "backup"
  | "performance";

interface TabDef {
  readonly key: TabKey;
  readonly label: string;
  readonly icon: typeof Key;
}

const TABS: readonly TabDef[] = [
  { key: "api-keys", label: "Cles API", icon: Key },
  { key: "webhooks", label: "Webhooks", icon: Webhook },
  { key: "flags", label: "Feature Flags", icon: Flag },
  { key: "gdpr", label: "RGPD", icon: Shield },
  { key: "backup", label: "Sauvegardes", icon: Database },
  { key: "performance", label: "Performance", icon: Activity },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d: string | null): string {
  if (!d) return "\u2014";
  return new Date(d).toLocaleDateString("fr-SN");
}

function formatDateTime(d: string | null): string {
  if (!d) return "\u2014";
  return new Date(d).toLocaleString("fr-SN");
}

// ---------------------------------------------------------------------------
// Error wrapper
// ---------------------------------------------------------------------------

function TabError({ message }: { readonly message?: string }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
      <p className="text-sm text-amber-800">
        {message ?? "Impossible de charger les données. Veuillez réessayer."}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// API Keys tab
// ---------------------------------------------------------------------------

function ApiKeysTab() {
  const { data, isLoading, isError } = useEnterpriseApiKeys();

  if (isError) return <TabError />;

  const keys = data?.keys ?? [];

  const columns: readonly ColumnDef<EnterpriseApiKey>[] = [
    {
      id: "name",
      header: "Nom",
      cell: (row) => (
        <span className="font-medium text-stone-900">{row.name}</span>
      ),
    },
    {
      id: "prefix",
      header: "Prefixe",
      cell: (row) => (
        <code className="rounded bg-stone-100 px-2 py-0.5 font-mono text-xs text-stone-700">
          {row.prefix}...
        </code>
      ),
    },
    {
      id: "scopes",
      header: "Portees",
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.scopes.map((s) => (
            <span
              key={s}
              className="inline-flex items-center rounded border border-stone-200 bg-stone-50 px-1.5 py-0.5 font-mono text-xs text-stone-600"
            >
              {s}
            </span>
          ))}
        </div>
      ),
    },
    {
      id: "created_at",
      header: "Creee le",
      cell: (row) => (
        <span className="text-xs text-stone-500">{formatDate(row.created_at)}</span>
      ),
    },
    {
      id: "last_used_at",
      header: "Derniere utilisation",
      cell: (row) => (
        <span className="text-xs text-stone-500">
          {formatDateTime(row.last_used_at)}
        </span>
      ),
    },
    {
      id: "is_active",
      header: "Statut",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            row.is_active
              ? "bg-emerald-100 text-emerald-700"
              : "bg-stone-100 text-stone-500"
          }`}
        >
          {row.is_active ? "Actif" : "Revoquee"}
        </span>
      ),
    },
  ];

  return (
    <AdminDataTable
      columns={columns}
      data={keys}
      isLoading={isLoading}
      emptyMessage="Aucune clé API trouvée."
    />
  );
}

// ---------------------------------------------------------------------------
// Webhooks tab
// ---------------------------------------------------------------------------

function WebhooksTab() {
  const { data, isLoading, isError } = useEnterpriseWebhooks();

  if (isError) return <TabError />;

  const webhooks = data?.webhooks ?? [];

  const columns: readonly ColumnDef<EnterpriseWebhook>[] = [
    {
      id: "url",
      header: "URL",
      cell: (row) => (
        <code className="break-all font-mono text-xs text-stone-700">
          {row.url}
        </code>
      ),
    },
    {
      id: "events",
      header: "Evenements",
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.events.map((e) => (
            <span
              key={e}
              className="inline-flex items-center rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700"
            >
              {e}
            </span>
          ))}
        </div>
      ),
    },
    {
      id: "is_active",
      header: "Actif",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            row.is_active
              ? "bg-emerald-100 text-emerald-700"
              : "bg-stone-100 text-stone-500"
          }`}
        >
          {row.is_active ? "Actif" : "Inactif"}
        </span>
      ),
    },
    {
      id: "last_triggered_at",
      header: "Dernier declenchement",
      cell: (row) => (
        <span className="text-xs text-stone-500">
          {formatDateTime(row.last_triggered_at)}
        </span>
      ),
    },
    {
      id: "failure_count",
      header: "Echecs",
      cell: (row) => (
        <span
          className={`text-xs font-medium ${
            row.failure_count > 0 ? "text-red-600" : "text-stone-400"
          }`}
        >
          {row.failure_count}
        </span>
      ),
    },
  ];

  return (
    <AdminDataTable
      columns={columns}
      data={webhooks}
      isLoading={isLoading}
      emptyMessage="Aucun webhook configure."
    />
  );
}

// ---------------------------------------------------------------------------
// Feature Flags tab
// ---------------------------------------------------------------------------

function FeatureFlagsTab() {
  const { data, isLoading, isError } = useFeatureFlags();

  if (isError) return <TabError />;

  const flags = data?.flags ?? [];

  const columns: readonly ColumnDef<FeatureFlag>[] = [
    {
      id: "name",
      header: "Nom",
      cell: (row) => (
        <div>
          <span className="font-medium text-stone-900">{row.name}</span>
          <p className="text-xs text-stone-400">{row.description}</p>
        </div>
      ),
    },
    {
      id: "enabled",
      header: "Active",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            row.enabled
              ? "bg-emerald-100 text-emerald-700"
              : "bg-stone-100 text-stone-500"
          }`}
        >
          {row.enabled ? "Oui" : "Non"}
        </span>
      ),
    },
    {
      id: "environment",
      header: "Environnement",
      cell: (row) => (
        <span className="inline-flex items-center rounded border border-stone-200 bg-stone-50 px-2 py-0.5 text-xs text-stone-600">
          {row.environment}
        </span>
      ),
    },
    {
      id: "rollout_percent",
      header: "Deploiement",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <div className="h-2 w-20 overflow-hidden rounded-full bg-stone-100">
            <div
              className="h-full rounded-full bg-amber-500 transition-all"
              style={{ width: `${row.rollout_percent}%` }}
            />
          </div>
          <span className="text-xs font-medium text-stone-600">
            {row.rollout_percent}%
          </span>
        </div>
      ),
    },
    {
      id: "updated_at",
      header: "Modifie le",
      cell: (row) => (
        <span className="text-xs text-stone-500">{formatDate(row.updated_at)}</span>
      ),
    },
  ];

  return (
    <AdminDataTable
      columns={columns}
      data={flags}
      isLoading={isLoading}
      emptyMessage="Aucun feature flag configure."
    />
  );
}

// ---------------------------------------------------------------------------
// GDPR tab
// ---------------------------------------------------------------------------

const GDPR_STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  processing: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

const GDPR_STATUS_LABELS: Record<string, string> = {
  pending: "En attente",
  processing: "En cours",
  completed: "Terminée",
  rejected: "Rejetée",
};

function GDPRTab() {
  const { data, isLoading, isError } = useGDPRRequests();

  if (isError) return <TabError />;

  const requests = data?.requests ?? [];

  const columns: readonly ColumnDef<GDPRRequest>[] = [
    {
      id: "type",
      header: "Type",
      cell: (row) => (
        <span className="font-medium capitalize text-stone-900">{row.type}</span>
      ),
    },
    {
      id: "subject_email",
      header: "Email",
      cell: (row) => (
        <span className="font-mono text-xs text-stone-600">
          {row.subject_email}
        </span>
      ),
    },
    {
      id: "status",
      header: "Statut",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
            GDPR_STATUS_COLORS[row.status] ??
            "bg-stone-100 text-stone-600 border-stone-200"
          }`}
        >
          {GDPR_STATUS_LABELS[row.status] ?? row.status}
        </span>
      ),
    },
    {
      id: "submitted_at",
      header: "Soumise le",
      cell: (row) => (
        <span className="text-xs text-stone-500">
          {formatDate(row.submitted_at)}
        </span>
      ),
    },
  ];

  return (
    <AdminDataTable
      columns={columns}
      data={requests}
      isLoading={isLoading}
      emptyMessage="Aucune demande RGPD trouvée."
    />
  );
}

// ---------------------------------------------------------------------------
// Backup tab
// ---------------------------------------------------------------------------

const BACKUP_STATUS_COLORS: Record<string, string> = {
  scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  running: "bg-amber-100 text-amber-800 border-amber-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  failed: "bg-red-100 text-red-800 border-red-200",
};

const BACKUP_STATUS_LABELS: Record<string, string> = {
  scheduled: "Programmée",
  running: "En cours",
  completed: "Terminée",
  failed: "Échouée",
};

function BackupTab() {
  const { data, isLoading, isError } = useBackup();

  if (isError) return <TabError />;

  const jobs = data?.jobs ?? [];
  const schedule = data?.schedule;

  const columns: readonly ColumnDef<BackupJob>[] = [
    {
      id: "created_at",
      header: "Date",
      cell: (row) => (
        <span className="text-xs text-stone-500">
          {formatDateTime(row.created_at)}
        </span>
      ),
    },
    {
      id: "type",
      header: "Type",
      cell: (row) => (
        <span className="inline-flex items-center rounded border border-stone-200 bg-stone-50 px-2 py-0.5 text-xs text-stone-600">
          {row.type}
        </span>
      ),
    },
    {
      id: "size_mb",
      header: "Taille",
      cell: (row) => (
        <span className="text-xs text-stone-500">
          {row.size_mb != null ? `${row.size_mb} Mo` : "\u2014"}
        </span>
      ),
    },
    {
      id: "status",
      header: "Statut",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
            BACKUP_STATUS_COLORS[row.status] ??
            "bg-stone-100 text-stone-600 border-stone-200"
          }`}
        >
          {BACKUP_STATUS_LABELS[row.status] ?? row.status}
        </span>
      ),
    },
    {
      id: "error",
      header: "Erreur",
      cell: (row) => (
        <span className="text-xs text-red-600">{row.error ?? ""}</span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Schedule summary */}
      {schedule && (
        <div className="rounded-lg border border-stone-200 bg-white p-4">
          <h3 className="mb-2 text-sm font-semibold text-stone-700">
            Planification
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
            <div>
              <p className="text-xs text-stone-400">Frequence</p>
              <p className="font-medium text-stone-900">{schedule.frequency}</p>
            </div>
            <div>
              <p className="text-xs text-stone-400">Heure</p>
              <p className="font-medium text-stone-900">{schedule.time}</p>
            </div>
            <div>
              <p className="text-xs text-stone-400">Retention</p>
              <p className="font-medium text-stone-900">
                {schedule.retention_days} jours
              </p>
            </div>
            <div>
              <p className="text-xs text-stone-400">Active</p>
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  schedule.enabled
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-stone-100 text-stone-500"
                }`}
              >
                {schedule.enabled ? "Oui" : "Non"}
              </span>
            </div>
          </div>
        </div>
      )}

      <AdminDataTable
        columns={columns}
        data={jobs}
        isLoading={isLoading}
        emptyMessage="Aucune sauvegarde trouvée."
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Performance tab
// ---------------------------------------------------------------------------

const METRIC_STATUS_COLORS: Record<string, string> = {
  ok: "text-emerald-600",
  warning: "text-amber-600",
  critical: "text-red-600",
};

function PerformanceTab() {
  const { data, isLoading, isError } = usePerformanceMetrics();

  if (isError) return <TabError />;

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-24 animate-pulse rounded-lg border border-stone-200 bg-stone-50"
          />
        ))}
      </div>
    );
  }

  const metrics = data?.metrics ?? [];
  const topEndpoints = data?.top_endpoints ?? [];

  return (
    <div className="space-y-6">
      {/* Metric cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {metrics.map((m) => (
          <div
            key={m.name}
            className="rounded-lg border border-stone-200 bg-white p-4"
          >
            <p className="text-xs text-stone-400">{m.name}</p>
            <p
              className={`mt-1 text-xl font-semibold ${
                METRIC_STATUS_COLORS[m.status] ?? "text-stone-900"
              }`}
            >
              {m.value}
              <span className="ml-1 text-xs font-normal text-stone-400">
                {m.unit}
              </span>
            </p>
            <p className="mt-0.5 text-xs text-stone-400">
              Seuil : {m.threshold} {m.unit}
            </p>
          </div>
        ))}
      </div>

      {/* Top endpoints table */}
      {topEndpoints.length > 0 && (
        <div>
          <h3 className="mb-3 text-sm font-semibold text-stone-700">
            Endpoints les plus sollicites
          </h3>
          <div className="overflow-x-auto rounded-lg border border-stone-200">
            <table className="min-w-full divide-y divide-stone-200 text-sm">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-stone-500">
                    Methode
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-stone-500">
                    Chemin
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-stone-500">
                    Moy. (ms)
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-stone-500">
                    P95 (ms)
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-stone-500">
                    Erreurs (%)
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-stone-500">
                    Appels/h
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 bg-white">
                {topEndpoints.map((ep, idx) => (
                  <tr key={`${ep.method}-${ep.path}-${idx}`}>
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center rounded border border-stone-200 bg-stone-50 px-1.5 py-0.5 font-mono text-xs text-stone-600">
                        {ep.method}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-stone-700">
                      {ep.path}
                    </td>
                    <td className="px-4 py-2 text-right text-xs text-stone-600">
                      {ep.avg_response_ms}
                    </td>
                    <td className="px-4 py-2 text-right text-xs text-stone-600">
                      {ep.p95_ms}
                    </td>
                    <td
                      className={`px-4 py-2 text-right text-xs font-medium ${
                        ep.error_rate_percent > 5
                          ? "text-red-600"
                          : "text-stone-600"
                      }`}
                    >
                      {ep.error_rate_percent}%
                    </td>
                    <td className="px-4 py-2 text-right text-xs text-stone-600">
                      {ep.calls_per_hour}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function EnterpriseHub() {
  const [activeTab, setActiveTab] = useState<TabKey>("api-keys");

  const handleTabChange = useCallback((tab: TabKey) => {
    setActiveTab(tab);
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Entreprise
            </h1>
            <p className="text-sm text-stone-500">
              Configuration entreprise, API, webhooks et integrations.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-stone-200">
          <nav className="-mb-px flex gap-4 overflow-x-auto" aria-label="Onglets">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`flex shrink-0 items-center gap-2 border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                    isActive
                      ? "border-amber-500 text-amber-600"
                      : "border-transparent text-stone-500 hover:border-stone-300 hover:text-stone-700"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab content */}
        {activeTab === "api-keys" && <ApiKeysTab />}
        {activeTab === "webhooks" && <WebhooksTab />}
        {activeTab === "flags" && <FeatureFlagsTab />}
        {activeTab === "gdpr" && <GDPRTab />}
        {activeTab === "backup" && <BackupTab />}
        {activeTab === "performance" && <PerformanceTab />}
      </div>
    </AdminLayout>
  );
}
